import { Paper, Stack, Text } from '@mantine/core';
import { ReactElement, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { TimelineSkeleton } from '@/components/TimelineSkeleton';
import { TimelineItems } from '@/components/Timeline/TimelineItems';
import { groupTimelineItems } from '@/services/Timeline/timelineGroups';
import { useCopyDirectLink } from '@/hooks/useCopyDirectLink';
import { useTimelineFilters } from '@/hooks/useTimelineFilters';
import { TIMELINE_FOCUS_ITEM_QUERY_PARAM } from '@bariendo/lib/constants';
import { TimelineMessageBox } from '@/components/Timeline/TimelineMessageBox';
import { TimelineFilters } from '@/components/Timeline/TimelineFilters';
import { EnabledTopic } from '@/services/timeline-threads';
import { useTimelineContext } from '@/hooks/useTimelineContext';

export function PartnerTimelineContent(): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const scrollToRef = searchParams.get(TIMELINE_FOCUS_ITEM_QUERY_PARAM);

  const { timelineContext } = useTimelineContext();
  const {
    filteredTimelineItems,
    filteredTimelineNotes,
    error,
    hasLoadedInitialData,
    activeFilter,
    searchTerm,
    startDate,
    endDate,
    selectedProvider,
  } = timelineContext;

  const timelineGroups = useMemo(() => {
    return groupTimelineItems({
      timelineRegisters: filteredTimelineItems,
      timelineNotes: filteredTimelineNotes,
      encounters: [],
    });
  }, [filteredTimelineItems, filteredTimelineNotes]);

  const { copyDirectLink } = useCopyDirectLink({
    paramName: TIMELINE_FOCUS_ITEM_QUERY_PARAM,
    errorTitle: "Couldn't copy link",
    errorMessage: "We couldn't copy the direct link to your clipboard.",
    trackingEnabled: false,
  });

  const handleNavigateToItem = useCallback(
    (itemReference: string) => {
      const currentUrl = new URL(window.location.href);
      const newSearchParams = new URLSearchParams();
      newSearchParams.set(TIMELINE_FOCUS_ITEM_QUERY_PARAM, itemReference);
      navigate(currentUrl.pathname + '?' + newSearchParams.toString());
    },
    [navigate]
  );

  const filterHandlers = useTimelineFilters({
    activeFilter,
    searchTerm,
    startDate,
    endDate,
    selectedProvider,
  });

  const enabledTopics: EnabledTopic[] = [];

  return (
    <Stack style={{ flex: 1 }} bg="gray.1">
      <TimelineFilters
        activeFilter={activeFilter}
        onFilterChange={filterHandlers.handleFilterChange}
        searchTerm={filterHandlers.localSearchTerm}
        onSearchChange={filterHandlers.handleSearchChange}
        selectedProvider={selectedProvider}
        onProviderChange={filterHandlers.handleProviderChange}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={filterHandlers.handleStartDateChange}
        onEndDateChange={filterHandlers.handleEndDateChange}
        context="Partner"
      />
      <Stack p="md">
        <TimelineMessageBox enabledTopics={enabledTopics} handleNavigateToItem={handleNavigateToItem} />
        {error ? (
          <Paper p="xl" ta="center">
            <Text c="red">Couldn't load timeline</Text>
            <Text size="xs" c="dimmed" mt="xs">
              {error instanceof Error ? error.message : 'Unknown error'}
            </Text>
          </Paper>
        ) : !hasLoadedInitialData ? (
          <TimelineSkeleton />
        ) : timelineGroups.length === 0 ? (
          <Paper p="xl" ta="center">
            <Text c="dimmed">
              {searchTerm ? 'No communications found matching your search' : 'No communications found for this partner'}
            </Text>
          </Paper>
        ) : (
          <TimelineItems
            timelineGroups={timelineGroups}
            highlightRef={scrollToRef}
            copyDirectLink={copyDirectLink}
            hasActiveFilters={filterHandlers.hasActiveFilters}
            handleNavigateToItem={handleNavigateToItem}
            searchTerm={searchTerm}
          />
        )}
      </Stack>
    </Stack>
  );
}
